import React, { useMemo, useState } from 'react';
import { Box, Typography, Grid, Modal, Paper, Link } from '@mui/material';
import CustomerModal from '../CustomerModal';
import { computePositivityScore } from '../../utils/scoring';

// Collect all instances in a cell, and derive avg importance, mentions (count), and score
const collectPositivesCell = (data, solutionCategory, positiveCategory) => {
  const instances = data
    .filter(
      (item) =>
        item.solution_choice_category === solutionCategory &&
        Array.isArray(item.positives_of_solution_choice) &&
        item.positives_of_solution_choice.some(
          (p) => p.positive_category === positiveCategory
        )
    )
    .flatMap((item) =>
      item.positives_of_solution_choice
        .filter((p) => p.positive_category === positiveCategory)
        .map((p) => ({
          ...p,
          customer_name: item.customer_name,
          customer_data: item,
        }))
    );

  if (!instances.length) {
    return { avgImportance: null, mentions: 0, score: 0, items: [] };
  }

  const sumImp = instances.reduce((acc, p) => acc + (Number(p.positive_importance) || 0), 0);
  const avgImportance = sumImp / instances.length;
  const mentions = instances.length;
  const score = computePositivityScore({ averageImportance: avgImportance, mentions });

  return { avgImportance, mentions, score, items: instances };
};

const PositivesHeatmap = ({ filteredData }) => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedPositives, setSelectedPositives] = useState([]);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const solutionCategories = [...new Set(filteredData.map((item) => item.solution_choice_category))];
  const positiveCategories = [
    ...new Set(
      filteredData.flatMap((item) =>
        (item.positives_of_solution_choice || []).map((p) => p.positive_category)
      )
    ),
  ];

  // Precompute the score range (min/max) for color scale using *scores* (not averages)
  const { minScore, maxScore, cellMap } = useMemo(() => {
    const map = new Map(); // key = `${posCat}__${solution}`
    let min = Infinity;
    let max = -Infinity;

    for (const pos of positiveCategories) {
      for (const sol of solutionCategories) {
        const cell = collectPositivesCell(filteredData, sol, pos);
        map.set(`${pos}__${sol}`, cell);
        if (cell.mentions > 0) {
          min = Math.min(min, cell.score);
          max = Math.max(max, cell.score);
        }
      }
    }
    if (min === Infinity) min = 0;
    if (max === -Infinity) max = 0;
    return { minScore: min, maxScore: max, cellMap: map };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredData, JSON.stringify(solutionCategories), JSON.stringify(positiveCategories)]);

  // Greenish intensity by score (light for low scores; stronger for high)
  const getCellColor = (score) => {
    if (!score || (maxScore === minScore)) return '#EAEAEA';
    const t = (score - minScore) / (maxScore - minScore);
    // Start near white → go greener
    const r = Math.round(255 - 155 * t);
    const g = 255;
    const b = Math.round(255 - 155 * t);
    return `rgb(${r}, ${g}, ${b})`;
  };

  const handleCellClick = (items) => {
    if (items?.length) {
      setSelectedPositives(items);
      setOpenModal(true);
    }
  };

  const handleClose = () => setOpenModal(false);
  const handleCustomerClick = (customer) => {
    setSelectedCustomer(customer);
    setCustomerModalOpen(true);
  };
  const handleCustomerModalClose = () => setCustomerModalOpen(false);

  return (
    <Box>
      {/* header row */}
      <Grid container>
        <Grid item xs={2}></Grid>
        {solutionCategories.map((solution, index) => (
          <Grid item xs key={`header-${index}`}>
            <Typography variant="subtitle2" align="center">{solution}</Typography>
          </Grid>
        ))}
      </Grid>

      {/* body */}
      {positiveCategories.map((positiveCategory, rowIndex) => (
        <Grid container key={`row-${rowIndex}`}>
          <Grid item xs={2}>
            <Typography variant="subtitle2">{positiveCategory}</Typography>
          </Grid>

          {solutionCategories.map((solutionCategory, colIndex) => {
            const { avgImportance, mentions, score, items } =
              cellMap.get(`${positiveCategory}__${solutionCategory}`) || { avgImportance: null, mentions: 0, score: 0, items: [] };
            const cellColor = getCellColor(score);

            return (
              <Grid
                item
                xs
                key={`cell-${rowIndex}-${colIndex}`}
                style={{
                  backgroundColor: cellColor,
                  border: '1px solid #ccc',
                  padding: '8px',
                  textAlign: 'center',
                  cursor: items.length > 0 ? 'pointer' : 'default',
                }}
                onClick={() => handleCellClick(items)}
              >
                {mentions > 0 ? (
                  <>
                    {/* Primary number: Positivity Score */}
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      Positivity score: {Math.round(score)}
                    </Typography>
                    {/* Subtext: avg importance & #convos */}
                    <Typography variant="caption" style={{ fontSize: '0.75rem' }}>
                      Importance {avgImportance.toFixed(2)} · {mentions} {mentions === 1 ? 'conversation' : 'conversations'}
                    </Typography>
                  </>
                ) : (
                  'n/a'
                )}
              </Grid>
            );
          })}
        </Grid>
      ))}

      {/* modal */}
      <Modal open={openModal} onClose={handleClose}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '80%', bgcolor: 'background.paper', boxShadow: 24, p: 4, maxHeight: '80vh', overflowY: 'auto' }}>
          <Typography variant="h6">Positive Details</Typography>
          {selectedPositives.map((positive, index) => (
            <Paper key={index} sx={{ p: 2, mb: 2 }}>
              <Typography variant="body1" fontWeight="bold" gutterBottom>
                "{positive.positive_importance_quote}"
              </Typography>
              <Typography variant="h6" gutterBottom>{positive.positive}</Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2">Category: {positive.positive_category}</Typography>
                <Typography variant="body2">Importance: {positive.positive_importance}</Typography>
                <Typography variant="body2">Justification: {positive.positive_importance_justification}</Typography>
                <Link href="#" variant="body2" underline="hover" sx={{ color: 'secondary.main', display: 'block', mb: 1 }}>
                  Timestamps: {positive.positive_timestamps}
                </Link>
              </Box>

              <Typography
                variant="body2"
                sx={{ cursor: 'pointer', color: 'primary.main', mt: 2 }}
                onClick={() => handleCustomerClick(positive.customer_data)}
              >
                Customer: {positive.customer_name}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Modal>

      <CustomerModal open={customerModalOpen} onClose={handleCustomerModalClose} customer={selectedCustomer} />
    </Box>
  );
};

export default PositivesHeatmap;
